import { useCallback, useRef } from "react";
import { useLatestRef } from "./useLatestRef";

/**
 * Serialise an async operation so overlapping invocations don't race.
 *
 * The returned ``trigger`` function schedules ``fn(arg)``:
 *
 * - If no invocation is in flight, ``fn`` runs immediately.
 * - If an invocation is already running, the caller's ``arg`` replaces
 *   any previously-queued arg and is processed once the in-flight call
 *   completes. Intermediate args are dropped — only the *latest* queued
 *   arg runs. This is the right policy when each invocation is a
 *   desired-state snapshot (e.g. "swap to Planned intake", "process the
 *   latest cell edit") rather than an independent event that must run.
 *
 * ``fn`` is expected to read the freshest state at execution time. The
 * hook does not pass stale closures — it forwards ``arg`` verbatim, and
 * each call of ``fn`` re-reads any refs/grids it depends on.
 *
 * Errors from ``fn`` are swallowed after logging to the console so an
 * unhandled rejection doesn't crash the UI — callers are expected to
 * surface their own user-visible error via ``meta.errorMessage`` on the
 * underlying mutation or equivalent. Swallowing here keeps the
 * "serial" state machine consistent: the next queued invocation still
 * runs even when the prior one rejects.
 *
 * @example Cell-edit queue
 * ```ts
 * const handleCellEdit = useSerialAsync(async (event: CellValueChangedEvent) => {
 *   const data = extractFromGrid(event.api);
 *   await saveMutation.mutateAsync(data);
 * });
 * ```
 *
 * @example Toggle queue
 * ```ts
 * const triggerToggle = useSerialAsync(handleToggle);
 * useEffect(() => {
 *   if (mode === prevRef.current) return;
 *   prevRef.current = mode;
 *   triggerToggle(mode);
 * }, [mode, triggerToggle]);
 * ```
 */
export function useSerialAsync<T>(
  fn: (arg: T) => Promise<void>
): (arg: T) => void {
  // The trigger keeps a stable identity across renders and calls the latest fn.
  const fnRef = useLatestRef(fn);

  const inFlightRef = useRef(false);
  const queuedRef = useRef<{ arg: T } | null>(null);

  return useCallback(
    function trigger(arg: T): void {
      if (inFlightRef.current) {
        queuedRef.current = { arg };
        return;
      }
      inFlightRef.current = true;
      // ``.catch`` absorbs rejections so the serial state machine doesn't
      // produce an unhandled-rejection when ``fn`` fails — error reporting
      // is the caller's responsibility (see the file-level doc). ``.finally``
      // then runs the post-completion bookkeeping regardless of outcome.
      fnRef
        .current(arg)
        .catch(() => {})
        .finally(() => {
          inFlightRef.current = false;
          const queued = queuedRef.current;
          if (queued) {
            queuedRef.current = null;
            trigger(queued.arg);
          }
        });
    },
    [fnRef]
  );
}
